import md5 from 'blueimp-md5';
import jwtDecode from 'jwt-decode';
import { history } from 'umi';

import { authLogout, authRefreshToken } from '@/services/login';
import tokenManager from '@/services/token';
import {
  clearRestrictedBillingSession,
  clearUserAuthTokens,
  buildRefreshContext,
  buildSessionId,
  resolveRefreshSession,
  shouldPreserveAutomyOnMissingAppToken,
  clearSessionRefreshing,
  clearAppLoginFlags,
  clearSsoLoginFlags,
  dropAppSessionOnLogout,
  getCurrentSession,
  isActivePortalContext,
  stashPortalLoginConfig,
  getRefreshSessionId,
  readStoredSessionToken,
  getOperationalAuthBlock,
  getRefreshingStartedAt,
  getSessionItem,
  hasRestrictedBillingToken,
  markSessionRefreshing,
  isOperationalAccessBlockedResponse,
  isOperationalRefreshBlockedResponse,
  isSupersededByStoredToken,
  removeSessionItem,
  resolveOperationalBlockRoute,
  setAuthority,
  setCurrentSession,
  setGlobalItem,
  setOperationalAuthBlock,
  setRestrictedBillingSession,
  setItem,
  setSessionItem,
  STORAGE_KEYS,
} from '../auth';
import { buildLoginUrl, shouldSkipLoginRedirect } from '../utilities/redirect';
import { isUnansweredRequestError } from './retryStrategy';
import { getToken, isPublicFragment, isTokenExpired, removeTokenById } from './tokenManager';

let notifyContext = null;
export const setNotifyContext = (context) => (notifyContext = context);

// Mutex for preventing concurrent token refresh requests
let refreshPromise = null;
let isRefreshing = false;
let operationalBlockHandling = false;

export const REFRESH_RESULT = Object.freeze({
  SUCCESS: true,
  FAILED: false,
  OPERATIONAL_BLOCK: 'operational_block',
  // The server never answered (offline, timeout): the session is not proven dead.
  UNAVAILABLE: 'unavailable',
});

/**
 * Clear normal access/refresh tokens without redirecting to login.
 * Preserves sessionStorage entries needed for the operational block screen.
 */
const clearNormalAuthTokens = () => {
  const userId = getCurrentSession()?.userId;
  removeSessionItem(STORAGE_KEYS.AUTH_TOKEN);
  removeSessionItem(STORAGE_KEYS.AUTHORITY);
  removeSessionItem(STORAGE_KEYS.AUTH_USER);
  removeSessionItem(STORAGE_KEYS.USER_CONTEXT);
  removeSessionItem(STORAGE_KEYS.AUTH_TOKEN_REFRESH);
  removeSessionItem(STORAGE_KEYS.AUTH_REFRESHING);
  clearUserAuthTokens(userId);
  setCurrentSession(null);
};

const extractResponseBody = async (responseOrError) => {
  if (!responseOrError) return null;

  if (isOperationalAccessBlockedResponse(responseOrError)) {
    return responseOrError;
  }

  if (responseOrError.authResult) return responseOrError;

  const response = responseOrError.response || responseOrError;
  if (typeof response?.clone === 'function') {
    try {
      return await response.clone().json();
    } catch (e) {
      return null;
    }
  }

  if (responseOrError.data && typeof responseOrError.data === 'object') {
    return responseOrError.data;
  }

  return null;
};

/**
 * Handle auth 403 with authResult.credentialsValid = true (login or refresh).
 * Never opens the main shell. Restricted token (login only) stays in memory.
 *
 * @param {object} responseBody
 * @param {{ fromRefresh?: boolean }} [options]
 * @returns {Promise<typeof REFRESH_RESULT.OPERATIONAL_BLOCK>}
 */
export const handleOperationalAccessBlock = async (responseBody, options = {}) => {
  if (operationalBlockHandling) return REFRESH_RESULT.OPERATIONAL_BLOCK;
  operationalBlockHandling = true;

  try {
    if (options.fromRefresh) {
      isRefreshing = false;
      refreshPromise = null;
      removeSessionItem(STORAGE_KEYS.AUTH_REFRESHING);
      window.dispatch?.({ type: 'global/resetRefreshToken' });

      const { clearRequestQueue } = require('./requestQueue');
      clearRequestQueue(responseBody);
      // Refresh never emits restrictedToken — clear any stale recovery session.
      clearRestrictedBillingSession();
    }

    clearNormalAuthTokens();
    setOperationalAuthBlock(responseBody);

    const withRestricted = !options.fromRefresh && hasRestrictedBillingToken(responseBody);
    if (withRestricted) {
      setRestrictedBillingSession(responseBody);
    } else if (!options.fromRefresh) {
      clearRestrictedBillingSession();
    }

    const reduxPayload = {
      authResult: responseBody.authResult,
      code: responseBody.code,
      message: responseBody.message,
      status: responseBody.status,
      hasRestrictedToken: withRestricted,
    };

    try {
      window.dispatch?.({ type: 'auth/setOperationalBlock', payload: reduxPayload });
    } catch (e) {}

    try {
      const appInsightsService = require('@/utils/appInsights').default;
      const authResult = responseBody?.authResult || {};
      appInsightsService.trackEvent('OperationalAccessBlocked', {
        code: responseBody?.code,
        accessMode: authResult.accessMode,
        blockReason: authResult.blockReason,
        allowedActions: Array.isArray(authResult.allowedActions)
          ? authResult.allowedActions.join(',')
          : '',
        hasRestrictedToken: withRestricted,
        source: options.fromRefresh ? 'refresh' : options.source || 'login',
        surface: options.surface || (options.fromRefresh ? 'refresh-token' : 'main-app'),
      });
    } catch (e) {}

    const route = resolveOperationalBlockRoute(responseBody, {
      hasRestrictedToken: withRestricted,
    });
    const currentPath = history.location?.pathname;
    if (currentPath !== route) {
      history.replace(route);
    }

    return REFRESH_RESULT.OPERATIONAL_BLOCK;
  } finally {
    setTimeout(() => {
      operationalBlockHandling = false;
    }, 1500);
  }
};

/** @deprecated Prefer handleOperationalAccessBlock({ fromRefresh: true }) */
export const handleOperationalRefreshBlock = (responseBody) =>
  handleOperationalAccessBlock(responseBody, { fromRefresh: true });

export const cleanupLoginData = (useRedirect = false, options = {}) => {
  // Only keep portal branding on login if this tab was actually in a portal.
  // Leftover DVA/session PORTAL_CONFIG from an earlier visit must not steal
  // an Automy 401/logout onto /user/login?portal=.
  const keepPortal = isActivePortalContext();
  let portal = keepPortal ? getSessionItem(STORAGE_KEYS.PORTAL_CONFIG) : null;
  // Preserve operational block across sessionStorage.clear() so the block
  // screen can still read it if cleanup runs after a refresh block.
  const operationalBlock = getOperationalAuthBlock();
  const userId = getCurrentSession()?.userId;

  removeSessionItem(STORAGE_KEYS.AUTH_TOKEN);
  removeSessionItem(STORAGE_KEYS.AUTHORITY);
  removeSessionItem(STORAGE_KEYS.AUTH_USER);
  removeSessionItem(STORAGE_KEYS.USER_CONTEXT);
  removeSessionItem(STORAGE_KEYS.AUTH_TOKEN_REFRESH);
  removeSessionItem(STORAGE_KEYS.AUTH_REFRESHING);

  // The keys above only cover the active namespace. Credentials surviving in
  // the user's other namespaces (portal/app) would be restored as a live
  // session on the next read and leave the tab on a context the shell cannot
  // serve, so drop them all while keeping non-auth data like the portal config.
  clearUserAuthTokens(userId);

  setCurrentSession(null);
  sessionStorage.clear();

  if (operationalBlock) {
    setOperationalAuthBlock(operationalBlock);
  }

  const currentPath = history.location?.pathname || window.location.pathname;
  const windowPath = window.location.pathname;
  const isOperationalBlockUrl =
    currentPath === '/user/login/access-blocked' || windowPath === '/user/login/access-blocked';

  let portalId = keepPortal ? options.portalId : undefined;
  if (portal) {
    try {
      portal = typeof portal === 'string' ? JSON.parse(portal) : portal;
      if (portalId == null) portalId = portal.id;
    } catch (e) {
      portal = undefined;
    }
  }

  if (keepPortal && portal) {
    stashPortalLoginConfig(portal);
  }

  const appMatch = (windowPath || currentPath)?.match(/^\/apps\/([^/]+)/);
  let appId = options.appId;
  if (!appId && appMatch) {
    appId = appMatch[1];
  }
  if (appId) {
    dropAppSessionOnLogout(appId);
    setGlobalItem(STORAGE_KEYS.APP_LOGIN_CONTEXT, appId);
  } else {
    clearAppLoginFlags();
  }
  clearSsoLoginFlags();

  // Do not bounce away from operational recovery screens to generic login.
  if (
    operationalBlock &&
    (isOperationalBlockUrl ||
      currentPath === '/support' ||
      currentPath?.startsWith('/billing/payment') ||
      windowPath === '/support' ||
      windowPath?.startsWith('/billing/payment'))
  ) {
    return;
  }

  const loginUrl = buildLoginUrl({
    preserveRedirect: useRedirect,
    portalId,
    appId,
  });

  // A second cleanup (refresh 401 then logout 401) must not overwrite a login
  // URL that already carries the original return path.
  if (shouldSkipLoginRedirect(loginUrl)) return;

  history.replace(loginUrl, history.location?.state || portal);
};

export const performLogout = async (useRedirect = false) => {
  // Store in raw sessionStorage — transient flag must survive session index clearing,
  // and cleanupLoginData's sessionStorage.clear() will naturally remove it.
  sessionStorage.setItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS, 'true');
  try {
    await authLogout();
  } finally {
    cleanupLoginData(useRedirect);
  }
};

/**
 * Safe logout function that checks logout flag to prevent concurrent logouts
 * @param {boolean} useRedirect - Whether to redirect after logout
 */
export const safeLogout = async (useRedirect = false) => {
  if (!sessionStorage.getItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS)) {
    try {
      await performLogout(useRedirect);
    } catch (e) {}
  }
};

export const handlePublicFragmentAuth = () => {
  const params = new URLSearchParams(window.location.search);
  const publicCode = params.get('publicCode');
  const code = params.get('code');
  const type = publicCode ? 'public' : 'external';
  const id = publicCode ?? md5(code || '');

  removeTokenById(type, id);

  if (!publicCode && code && window.location.pathname.startsWith('/external/')) {
    const handlePath = '/external/request-handle';
    if (!window.location.pathname.startsWith(handlePath)) {
      window.location.assign(`${handlePath}?code=${encodeURIComponent(code)}`);
      return;
    }
  }

  window.location.reload();
};

export const handleTokenRefresh = async (token) => {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  // Use the same lock name as the proactive path in token.js service so that both
  // mechanisms coordinate on a single named resource per session.
  // Multi-tab: tabs acquiring the same lock are serialized by the browser.
  const sessionId = getRefreshSessionId();
  const lockName = `token-refresh-${sessionId ?? 'default'}`;

  const run = async () => {
    if (isRefreshing && refreshPromise) return refreshPromise;
    isRefreshing = true;
    refreshPromise = performTokenRefresh(token);
    try {
      const result = await refreshPromise;
      if (result === REFRESH_RESULT.SUCCESS) {
        const { getQueueLength, processRequestQueue } = require('./requestQueue');
        if (getQueueLength() > 0) processRequestQueue();
      }
      return result;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  };

  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request(lockName, run);
  }
  return run();
};

const performTokenRefresh = async (token) => {
  const session = resolveRefreshSession();
  const readSessionToken = () => (session?.userId ? readStoredSessionToken(session) : getToken());
  let sentToken = null;
  try {
    if (getRefreshingStartedAt(session?.type, session?.userId, session?.resourceId)) {
      // Another refresh is in flight — wait for it to complete before processing
      // the request queue. Returning true immediately causes the queue to replay
      // with the old token, resulting in a second wave of 401s.
      await new Promise((resolve) => {
        const interval = setInterval(() => {
          if (!getRefreshingStartedAt(session?.type, session?.userId, session?.resourceId)) {
            clearInterval(interval);
            resolve();
          }
        }, 200);
        setTimeout(() => {
          clearInterval(interval);
          resolve();
        }, 30_000);
      });
      const freshToken = readSessionToken();
      return !!freshToken?.accessToken;
    }

    // Re-read from the namespace we resolved — getToken() follows the index and
    // would refresh Automy with appId (or write Automy into APP) after a navigate.
    const freshToken = readSessionToken() || token;

    // If the token was refreshed very recently (by the proactive worker path or
    // another tab), skip the redundant refresh — just confirm the token is valid.
    if (freshToken?.issuedAt && freshToken?.expiresIn) {
      const expiresAt = freshToken.issuedAt + freshToken.expiresIn * 1000;
      const timeUntilExpiry = expiresAt - Date.now();
      if (timeUntilExpiry > 5 * 60 * 1000 + 30_000) {
        return true;
      }
    }

    markSessionRefreshing(session?.type, session?.userId, session?.resourceId);
    window.dispatch?.({ type: 'global/willRefreshToken' });

    const refreshContext = buildRefreshContext(session);
    sentToken = freshToken;
    const refreshedToken = await authRefreshToken(freshToken, refreshContext);
    removeSessionItem(STORAGE_KEYS.USER_CONTEXT);

    // A company/environment switch or another tab replaced the token while this
    // request was out. Writing this response over it would put every tab back
    // on whatever tenant the outgoing token carried.
    if (isSupersededByStoredToken(freshToken, readSessionToken())) {
      window.dispatch?.({ type: 'global/resetRefreshToken' });
      return REFRESH_RESULT.SUCCESS;
    }

    if (refreshedToken?.accessToken) {
      const user = jwtDecode(refreshedToken.accessToken);
      const serialized = JSON.stringify({ ...refreshedToken, issuedAt: Date.now() });
      if (session?.userId) {
        setItem(
          session.type,
          session.userId,
          session.resourceId,
          STORAGE_KEYS.AUTH_TOKEN,
          serialized,
        );
      } else {
        setSessionItem(STORAGE_KEYS.AUTH_TOKEN, serialized);
      }

      const active = getCurrentSession();
      const writesToActive =
        !session?.userId ||
        (active &&
          active.type === session.type &&
          String(active.userId) === String(session.userId) &&
          String(active.resourceId || '') === String(session.resourceId || ''));

      if (writesToActive) {
        setSessionItem(STORAGE_KEYS.AUTH_TOKEN_REFRESH, 'true');
        window.dispatch?.({ type: 'userContext/defaultContext', payload: user });
        setAuthority(Array.isArray(user.roles) ? user.roles : JSON.parse(user.roles));
      }
      try {
        if (refreshedToken.expiresIn) {
          const expiresAt = Date.now() + refreshedToken.expiresIn * 1000;
          const sessionId =
            (session?.userId && buildSessionId(session.type, session.userId, session.resourceId)) ||
            getRefreshSessionId();
          await tokenManager.updateToken(sessionId, refreshedToken.accessToken, expiresAt);
        }
        tokenManager.requestSocketReconnect('token_refresh');
      } catch (e) {}

      window.dispatch?.({ type: 'global/resetRefreshToken' });
      return REFRESH_RESULT.SUCCESS;
    }

    window.dispatch?.({ type: 'global/resetRefreshToken' });
    const { clearRequestQueue } = require('./requestQueue');
    clearRequestQueue(new Error('Token refresh failed'));
    return REFRESH_RESULT.FAILED;
  } catch (e) {
    window.dispatch?.({ type: 'global/resetRefreshToken' });

    const responseBody = await extractResponseBody(e);
    if (isOperationalRefreshBlockedResponse(responseBody)) {
      return handleOperationalRefreshBlock(responseBody);
    }

    // A switch can void the refresh token this call sent; that is no reason to
    // end a session that already moved on to a valid token.
    if (isSupersededByStoredToken(sentToken, readSessionToken())) {
      return REFRESH_RESULT.SUCCESS;
    }

    const { clearRequestQueue } = require('./requestQueue');
    clearRequestQueue(e);
    return isUnansweredRequestError(e) ? REFRESH_RESULT.UNAVAILABLE : REFRESH_RESULT.FAILED;
  } finally {
    clearSessionRefreshing(session?.type, session?.userId, session?.resourceId);
  }
};

/**
 * Handle 401/403 authentication errors
 * @param {Response} response - HTTP response object
 * @param {AbortController} abortController - Abort controller
 * @param {Object} [requestOptions] - Options the failed request was sent with
 */
export const handleAuthError = async (response, abortController, requestOptions) => {
  abortController.abort();
  const token = getToken();

  if (response.status === 403) {
    const responseBody = await extractResponseBody(response);
    const isAuthEndpoint = response.url?.includes('/auth');
    const isRefresh = response.url?.includes('/refreshToken');

    if (isRefresh) {
      if (isOperationalRefreshBlockedResponse(responseBody)) {
        await handleOperationalAccessBlock(responseBody, { fromRefresh: true });
        return;
      }
      // A company/environment switch can void the refresh token this call
      // sent; the caller carries on with the token that replaced it.
      if (isSupersededByStoredToken(requestOptions?.data, token)) return;
      // Invalid/expired refresh token — fall through to normal session cleanup.
      cleanupLoginData(true);
      return;
    }

    // Login / 2FA / OAuth / SSO: operational block with optional restrictedToken.
    if (isAuthEndpoint && isOperationalAccessBlockedResponse(responseBody)) {
      await handleOperationalAccessBlock(responseBody, { fromRefresh: false });
      return;
    }

    if (isOperationalRefreshBlockedResponse(responseBody)) {
      await handleOperationalAccessBlock(responseBody, { fromRefresh: true });
      return;
    }

    history.replace('/exception/403');
    return;
  }

  if (
    response.url.includes('/refreshToken') ||
    (response.status === 401 && response.url.includes('/logout'))
  ) {
    if (isPublicFragment()) {
      handlePublicFragmentAuth();
      return;
    }
    cleanupLoginData(true);
    return;
  }

  if (token) {
    if (isPublicFragment()) handlePublicFragmentAuth();
    else if (token.refreshToken) {
      notifyContext?.notify?.('tokenRefresh');
      const refreshResult = await handleTokenRefresh(token);
      if (
        refreshResult === REFRESH_RESULT.OPERATIONAL_BLOCK ||
        refreshResult === REFRESH_RESULT.UNAVAILABLE
      ) {
        return;
      }
      if (!refreshResult) await performLogout(true);
    } else await performLogout(true);
  } else if (!isPublicFragment()) {
    // /apps/:id can 401 before the APP JWT exists. Do not wipe Automy — the
    // mint still needs it. AppGateway owns the login redirect if mint fails.
    if (shouldPreserveAutomyOnMissingAppToken()) return;
    // No usable token but the server still rejected us with 401. Without this
    // branch the app stays stuck on the 401 with no token to refresh and never
    // redirects. Clear any stale session remnants and send the user to login.
    cleanupLoginData(true);
  }
};

/**
 * Proactively ensure the current session token is still valid.
 *
 * Used by route guards and visibility handlers to close the "zombie session"
 * gap where an expired token sits in storage but no API call has triggered a
 * 401 yet — so the app keeps rendering as if logged in and never redirects.
 *
 * - Token valid (not expired): resolves true.
 * - Token expired with a refresh token: attempts a refresh; logs out on failure.
 * - Token expired without a refresh token: logs out.
 * - Operational block on refresh: resolves false without treating it as expired session.
 * - Refresh never answered (offline, timeout): resolves false and keeps the session,
 *   so waking a laptop before its Wi-Fi reconnects does not end it.
 *
 * @returns {Promise<boolean>} true only when the session ends up valid.
 */
export const ensureFreshSession = async () => {
  // Public/external fragments use their own token lifecycle — leave them alone.
  if (isPublicFragment()) return true;
  if (sessionStorage.getItem(STORAGE_KEYS.LOGOUT_IN_PROGRESS)) return false;
  if (operationalBlockHandling || getOperationalAuthBlock()) return false;

  const token = getToken();
  if (!token) return false;
  if (!isTokenExpired()) return true;

  if (token.refreshToken) {
    const refreshResult = await handleTokenRefresh(token);
    if (
      refreshResult === REFRESH_RESULT.OPERATIONAL_BLOCK ||
      refreshResult === REFRESH_RESULT.UNAVAILABLE
    ) {
      return false;
    }
    if (!refreshResult) await safeLogout(true);
    return !!refreshResult;
  }

  await safeLogout(true);
  return false;
};
